import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import type { TopographyPoint } from "@features/Visualization/renderers/topographyContracts";
import {
  idwInterpolateNormalized,
  liveScalpPointsToNormalizedElectrodes,
} from "./liveScalpLocalizeHelpers";

const COLOR_STOPS: Array<{ t: number; color: [number, number, number] }> = [
  { t: 0, color: [54, 90, 156] },
  { t: 0.22, color: [58, 153, 215] },
  { t: 0.5, color: [231, 240, 247] },
  { t: 0.74, color: [245, 187, 92] },
  { t: 1, color: [217, 92, 72] },
];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function sampleColor(normalized: number): [number, number, number] {
  const t = clamp(normalized, 0, 1);
  for (let i = 1; i < COLOR_STOPS.length; i += 1) {
    const prev = COLOR_STOPS[i - 1];
    const curr = COLOR_STOPS[i];
    if (t <= curr.t) {
      const mix = (t - prev.t) / Math.max(curr.t - prev.t, 1e-6);
      return [
        Math.round(prev.color[0] + (curr.color[0] - prev.color[0]) * mix),
        Math.round(prev.color[1] + (curr.color[1] - prev.color[1]) * mix),
        Math.round(prev.color[2] + (curr.color[2] - prev.color[2]) * mix),
      ];
    }
  }
  return COLOR_STOPS[COLOR_STOPS.length - 1].color;
}

export type LiveScalpInterpolatedMapHandle = {
  getCanvas: () => HTMLCanvasElement | null;
};

export type LiveScalpInterpolatedMapProps = {
  width: number;
  height: number;
  points: TopographyPoint[];
  minValue: number;
  maxValue: number;
  focusLabel?: string | null;
  onElectrodeClick?: (label: string) => void;
  resolution?: number;
  "data-testid"?: string;
};

/**
 * Client-side IDW interpolated scalp surface for Localize mode.
 */
const LiveScalpInterpolatedMap = forwardRef<
  LiveScalpInterpolatedMapHandle,
  LiveScalpInterpolatedMapProps
>(function LiveScalpInterpolatedMap(
  {
    width,
    height,
    points,
    minValue,
    maxValue,
    focusLabel = null,
    onElectrodeClick,
    resolution = 48,
    "data-testid": dataTestId = "live-scalp-interpolated-map",
  },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const electrodes = useMemo(
    () => liveScalpPointsToNormalizedElectrodes(points),
    [points],
  );

  useImperativeHandle(
    ref,
    () => ({
      getCanvas: () => canvasRef.current,
    }),
    [],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    canvas.width = width;
    canvas.height = height;
    ctx.clearRect(0, 0, width, height);

    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.42;
    const range = Math.max(maxValue - minValue, 1e-6);

    ctx.fillStyle = "rgba(12, 22, 36, 0.85)";
    ctx.fillRect(0, 0, width, height);

    if (electrodes.length >= 3) {
      const gridSize = Math.max(16, Math.min(resolution, 64));
      const gridCanvas = document.createElement("canvas");
      gridCanvas.width = gridSize;
      gridCanvas.height = gridSize;
      const gridCtx = gridCanvas.getContext("2d");
      if (gridCtx) {
        const image = gridCtx.createImageData(gridSize, gridSize);
        const data = image.data;
        for (let gy = 0; gy < gridSize; gy += 1) {
          for (let gx = 0; gx < gridSize; gx += 1) {
            const nx = gx / (gridSize - 1);
            const ny = gy / (gridSize - 1);
            const dx = nx * 2 - 1;
            const dy = ny * 2 - 1;
            if (dx * dx + dy * dy > 1.05) {
              continue;
            }
            const value = idwInterpolateNormalized(electrodes, nx, ny);
            const [r, g, b] = sampleColor((value - minValue) / range);
            const index = (gy * gridSize + gx) * 4;
            data[index] = r;
            data[index + 1] = g;
            data[index + 2] = b;
            data[index + 3] = 230;
          }
        }
        gridCtx.putImageData(image, 0, 0);
        ctx.save();
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, radius, radius * 1.02, 0, 0, Math.PI * 2);
        ctx.clip();
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(
          gridCanvas,
          centerX - radius,
          centerY - radius * 1.02,
          radius * 2,
          radius * 2.04,
        );
        ctx.restore();
      }
    }

    ctx.beginPath();
    ctx.ellipse(centerX, centerY, radius, radius * 1.02, 0, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(210, 230, 255, 0.55)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(centerX - radius * 0.12, centerY - radius * 0.92);
    ctx.lineTo(centerX, centerY - radius * 1.12);
    ctx.lineTo(centerX + radius * 0.12, centerY - radius * 0.92);
    ctx.strokeStyle = "rgba(210, 230, 255, 0.45)";
    ctx.stroke();

    for (const electrode of electrodes) {
      const ex = centerX + (electrode.x * 2 - 1) * radius;
      const ey = centerY + (electrode.y * 2 - 1) * radius * 1.02;
      const isFocus =
        focusLabel != null &&
        electrode.name.toUpperCase() === focusLabel.toUpperCase();
      ctx.beginPath();
      ctx.arc(ex, ey, isFocus ? 4.5 : 3, 0, Math.PI * 2);
      ctx.fillStyle = isFocus ? "#ffe08a" : "rgba(255,255,255,0.85)";
      ctx.fill();
      ctx.strokeStyle = isFocus ? "#c9a227" : "rgba(20,30,45,0.7)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }, [
    electrodes,
    focusLabel,
    height,
    maxValue,
    minValue,
    resolution,
    width,
  ]);

  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onElectrodeClick || electrodes.length === 0) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * width;
    const py = ((event.clientY - rect.top) / rect.height) * height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.42;

    let bestLabel: string | null = null;
    let bestDist = Infinity;
    for (const electrode of electrodes) {
      const ex = centerX + (electrode.x * 2 - 1) * radius;
      const ey = centerY + (electrode.y * 2 - 1) * radius * 1.02;
      const dist = Math.hypot(px - ex, py - ey);
      if (dist < bestDist) {
        bestDist = dist;
        bestLabel = electrode.name;
      }
    }
    if (bestLabel && bestDist <= 14) {
      onElectrodeClick(bestLabel);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="eeg-live-topography-card__interpolated"
      data-testid={dataTestId}
      role="img"
      aria-label="Interpolated scalp field map"
      onClick={handleClick}
      style={{
        width,
        height,
        cursor: onElectrodeClick ? "pointer" : "default",
        borderRadius: 6,
      }}
    />
  );
});

export default LiveScalpInterpolatedMap;
